import { useNavigate } from "react-router";
import type { Brand } from "@src/domain/entities/brand";
import type { Organization } from "@src/domain/entities/organization/Organization";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  Brand as BrandUi,
  Button,
  Division,
  Flex,
  Paragraph,
} from "@ir/ui";
import { BrandStatusControl } from "./BrandStatusControl";

export interface OrganizationsBrandListProps {
  brands: Brand[];
  onUpdateBrandStatus?: (brandId: number, brandIsActive: boolean) => Promise<void>;
  onUpdateSuccess?: () => void;
  organization: Organization;
}

export const OrganizationsBrandList = ({
  brands,
  onUpdateBrandStatus,
  onUpdateSuccess,
  organization,
}: OrganizationsBrandListProps) => {
  const navigate = useNavigate();

  if (!brands.length) {
    return (
      <Paragraph className="m-0 text-grey-500 font-regular">
        No brands for this organization.
      </Paragraph>
    );
  }

  return (
    <ErrorBoundary>
      {brands.map((brand) => {
        const displayName = brand.brandDisplayName ?? brand.brandName;
        return (
          <Flex key={brand.brandId} align="center" className="py-2">
            <BrandUi
              size="small"
              brandName={displayName}
              logoSrcCompact={brand.brandLogoUrl ?? undefined}
            />
            <Division style={{ marginLeft: "1rem", flex: 1 }}>
              <Paragraph
                className="text-grey-700 font-bold m-0"
                style={{ lineHeight: "1" }}
              >
                <Button
                  size="small"
                  noStyle
                  htmlTitle="View Brand Details"
                  className="link-button"
                  onPress={() =>
                    navigate(
                      `/organizations/${organization.organizationKey}/${brand.brandKey}`,
                      {
                        state: {
                          organizationId: organization.organizationId,
                        },
                      },
                    )
                  }
                >
                  {displayName}
                </Button>
              </Paragraph>
              {typeof brand.brandFeaturesCount === "number" && (
                <Paragraph
                  className="m-0 p-0 text-grey-500 font-regular text-sm"
                  style={{ fontSize: "0.65rem" }}
                >
                  {brand.brandFeaturesCount} Features enabled
                </Paragraph>
              )}
            </Division>
            <BrandStatusControl
              brandId={brand.brandId}
              brandIsActive={brand.brandIsActive}
              brandDeletedAt={brand.brandDeletedAt}
              onUpdateBrandStatus={onUpdateBrandStatus}
              onUpdateSuccess={onUpdateSuccess}
            />
          </Flex>
        );
      })}
    </ErrorBoundary>
  );
};
